/**
 * DevToolsWidget — React Scan–style floating dev panel.
 *
 * Combines Console, Subscription Override, and Index Debug into a single
 * draggable widget. Drag towards an edge to collapse into a small pill;
 * click the pill (or drag inward) to expand again.
 *
 * DEV ONLY — guarded by `import.meta.env.DEV` in App.tsx.
 */

import { memo, useCallback, useEffect, useRef, useState } from "react"
import {
  type DevSubscriptionMode,
  setDevSubscriptionOverride,
} from "@/features/settings/hooks/useCurrentSubscription"
import {
  type DbBackend,
  getDbBackendPref,
  setDbBackendPref,
  resolveBackend,
} from "@/db/sqliteInit"
import { isSqliteActive } from "@/db/sqliteDetect"

// ─── Constants ──────────────────────────────────────────────────────────

const SAFE = 8
const TOOLBAR_H = 36
const PANEL_W = 320
const PANEL_H = 280
const COLLAPSED = { w: 20, h: 44 } // pill size (horizontal orientation)
const LS_POS = "dev:widget-pos"
const LS_COLLAPSED = "dev:widget-collapsed"

// React Scan widget detection — avoid overlapping their canvas-rendered toolbar
// React Scan renders its UI on a full-screen canvas with z-index ~2147483600.
// Its toolbar floats at the top-right (~40px tall) by default in dev mode.
const REACT_SCAN_TOOLBAR_H = 56 // React Scan toolbar height + gap

function isReactScanActive(): boolean {
  // React Scan uses a fixed full-screen canvas with very high z-index
  const canvases = document.querySelectorAll("canvas")
  for (const c of canvases) {
    const cs = getComputedStyle(c)
    if (cs.position === "fixed" && parseInt(cs.zIndex) > 2147483500) {
      return true
    }
  }
  return false
}

function getReactScanOffset(position: "top" | "bottom"): number {
  if (!isReactScanActive()) return 0
  // React Scan toolbar renders at top-right by default
  if (position === "top") return REACT_SCAN_TOOLBAR_H
  return 0
}

type Corner = "top-left" | "top-right" | "bottom-left" | "bottom-right"
type ToolId = "console" | "subscription" | "index" | "db" | "device" | null

// ─── Console log capture ────────────────────────────────────────────────

const MAX_LINES = 80

interface LogEntry {
  id: number
  level: "log" | "warn" | "error" | "info"
  message: string
  ts: string
}

let logEntries: LogEntry[] = []
let logSeq = 0
let logListeners: Array<() => void> = []

function pushLog(level: LogEntry["level"], args: unknown[]) {
  const message = args
    .map((a) =>
      typeof a === "string"
        ? a
        : a instanceof Error
          ? a.message
          : JSON.stringify(a, null, 1),
    )
    .join(" ")
  const d = new Date()
  const ts = `${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}.${d.getMilliseconds().toString().padStart(3, "0")}`
  logEntries = [
    ...logEntries.slice(-(MAX_LINES - 1)),
    { id: ++logSeq, level, message, ts },
  ]
  for (const fn of logListeners) fn()
}

let patched = false
function patchConsole() {
  if (patched) return
  patched = true
  const orig = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    info: console.info.bind(console),
  }
  console.log = (...a: unknown[]) => {
    orig.log(...a)
    pushLog("log", a)
  }
  console.warn = (...a: unknown[]) => {
    orig.warn(...a)
    pushLog("warn", a)
  }
  console.error = (...a: unknown[]) => {
    orig.error(...a)
    pushLog("error", a)
  }
  console.info = (...a: unknown[]) => {
    orig.info(...a)
    pushLog("info", a)
  }
}

function useLogEntries() {
  const [, setTick] = useState(0)
  useEffect(() => {
    const fn = () => setTick((t) => t + 1)
    logListeners.push(fn)
    return () => {
      logListeners = logListeners.filter((l) => l !== fn)
    }
  }, [])
  return logEntries
}

// ─── Index debug toggle (mirrors DebugIndexBadge's global boolean) ──────

function dispatchIndexToggle() {
  document.dispatchEvent(
    new KeyboardEvent("keydown", {
      key: "D",
      code: "KeyD",
      ctrlKey: true,
      shiftKey: true,
      bubbles: true,
    }),
  )
}

// ─── Subscription modes ─────────────────────────────────────────────────

const SUB_MODES: { value: DevSubscriptionMode; label: string; emoji: string }[] =
  [
    { value: "real", label: "Real", emoji: "🔄" },
    { value: "paid", label: "Paid", emoji: "💳" },
    { value: "demo", label: "Demo", emoji: "🎭" },
    { value: "trial-long", label: "Trial 25d", emoji: "⏳" },
    { value: "trial-short", label: "Trial 3d", emoji: "⚠️" },
    { value: "free", label: "Free", emoji: "🚫" },
  ]

// ─── Safe area helpers ───────────────────────────────────────────────────
// Reads Capacitor CSS vars first, then falls back to browser env() values
// (which Safari sets when viewport-fit=cover). Takes the max of both.

let _envProbe: HTMLDivElement | null = null

function getEnvInset(side: "top" | "bottom" | "left" | "right"): number {
  if (!_envProbe) {
    _envProbe = document.createElement("div")
    _envProbe.style.cssText =
      "position:fixed;pointer-events:none;visibility:hidden;z-index:-1;"
    document.body.appendChild(_envProbe)
  }
  // Use padding to measure env() — getComputedStyle returns the resolved px
  _envProbe.style.paddingTop =
    side === "top" ? "env(safe-area-inset-top, 0px)" : "0px"
  _envProbe.style.paddingBottom =
    side === "bottom" ? "env(safe-area-inset-bottom, 0px)" : "0px"
  _envProbe.style.paddingLeft =
    side === "left" ? "env(safe-area-inset-left, 0px)" : "0px"
  _envProbe.style.paddingRight =
    side === "right" ? "env(safe-area-inset-right, 0px)" : "0px"

  const cs = getComputedStyle(_envProbe)
  const prop =
    side === "top"
      ? "paddingTop"
      : side === "bottom"
        ? "paddingBottom"
        : side === "left"
          ? "paddingLeft"
          : "paddingRight"
  return parseFloat(cs[prop]) || 0
}

function getSafeInsets() {
  const s = getComputedStyle(document.documentElement)
  const cap = {
    top: parseFloat(s.getPropertyValue("--status-bar-height")) || 0,
    bottom: parseFloat(s.getPropertyValue("--safe-area-bottom")) || 0,
    left: parseFloat(s.getPropertyValue("--safe-area-left")) || 0,
    right: parseFloat(s.getPropertyValue("--safe-area-right")) || 0,
  }
  return {
    top: Math.max(cap.top, getEnvInset("top")),
    bottom: Math.max(cap.bottom, getEnvInset("bottom")),
    left: Math.max(cap.left, getEnvInset("left")),
    right: Math.max(cap.right, getEnvInset("right")),
  }
}

// ─── Position helpers ───────────────────────────────────────────────────

function closestCorner(
  x: number,
  y: number,
  w: number,
  h: number,
): Corner {
  const cx = x + w / 2
  const cy = y + h / 2
  const mx = window.innerWidth / 2
  const my = window.innerHeight / 2
  if (cx < mx) return cy < my ? "top-left" : "bottom-left"
  return cy < my ? "top-right" : "bottom-right"
}

function snapPosition(corner: Corner, w: number, h: number) {
  const iw = window.innerWidth
  const ih = window.innerHeight
  const sa = getSafeInsets()
  const rsTop = getReactScanOffset("top")
  const rsBottom = getReactScanOffset("bottom")
  switch (corner) {
    case "top-left":
      return { x: SAFE + sa.left, y: SAFE + sa.top + rsTop }
    case "top-right":
      return { x: iw - w - SAFE - sa.right, y: SAFE + sa.top + rsTop }
    case "bottom-left":
      return { x: SAFE + sa.left, y: ih - h - SAFE - sa.bottom - rsBottom }
    case "bottom-right":
      return { x: iw - w - SAFE - sa.right, y: ih - h - SAFE - sa.bottom - rsBottom }
  }
}

function collapsedPos(corner: Corner) {
  const iw = window.innerWidth
  const ih = window.innerHeight
  const sa = getSafeInsets()
  const rsTop = getReactScanOffset("top")
  const rsBottom = getReactScanOffset("bottom")
  switch (corner) {
    case "top-left":
      return { x: -1, y: SAFE + sa.top + rsTop }
    case "top-right":
      return { x: iw - COLLAPSED.w + 1, y: SAFE + sa.top + rsTop }
    case "bottom-left":
      return { x: -1, y: ih - COLLAPSED.h - SAFE - sa.bottom - rsBottom }
    case "bottom-right":
      return { x: iw - COLLAPSED.w + 1, y: ih - COLLAPSED.h - SAFE - sa.bottom - rsBottom }
  }
}

function lsRead<T>(key: string): T | null {
  try {
    const v = localStorage.getItem(key)
    return v ? JSON.parse(v) : null
  } catch {
    return null
  }
}

function lsWrite(key: string, val: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(val))
  } catch {
    /* noop */
  }
}

// ─── Level colors ───────────────────────────────────────────────────────

const LEVEL_CLR: Record<LogEntry["level"], string> = {
  log: "#a1a1aa",
  info: "#60a5fa",
  warn: "#fbbf24",
  error: "#f87171",
}

// ─── Component ──────────────────────────────────────────────────────────

export const DevToolsWidget = memo(function DevToolsWidget() {
  const ref = useRef<HTMLDivElement>(null)
  const [tool, setTool] = useState<ToolId>(null)
  const [collapsed, setCollapsed] = useState<Corner | null>(
    () => lsRead<Corner>(LS_COLLAPSED),
  )
  const [corner, setCorner] = useState<Corner>(
    () => lsRead<{ corner: Corner }>(LS_POS)?.corner ?? "bottom-right",
  )
  const [subMode, setSubMode] = useState<DevSubscriptionMode>(
    () =>
      (localStorage.getItem("dev:subscriptionOverride") as DevSubscriptionMode) ||
      "real",
  )

  // Scroll ref for console
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)
  const entries = useLogEntries()

  useEffect(() => {
    patchConsole()
  }, [])

  // Auto-scroll console only if user hasn't scrolled up
  useEffect(() => {
    if (tool === "console" && scrollRef.current && stickToBottom.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [entries, tool])

  // ── Position on mount / resize ──────────────────────────────────────
  const applyPosition = useCallback(
    (animate = true) => {
      const el = ref.current
      if (!el) return

      if (collapsed) {
        const p = collapsedPos(collapsed)
        el.style.transition = animate
          ? "all 0.25s cubic-bezier(0,0,0.2,1)"
          : "none"
        el.style.width = `${COLLAPSED.w}px`
        el.style.height = `${COLLAPSED.h}px`
        el.style.transform = `translate3d(${p.x}px,${p.y}px,0)`
        el.style.borderRadius = collapsed.endsWith("right")
          ? "8px 0 0 8px"
          : "0 8px 8px 0"
      } else {
        const w = PANEL_W
        const h = tool ? TOOLBAR_H + PANEL_H : TOOLBAR_H
        const p = snapPosition(corner, w, h)
        el.style.transition = animate
          ? "all 0.25s cubic-bezier(0,0,0.2,1)"
          : "none"
        el.style.width = `${w}px`
        el.style.height = `${h}px`
        el.style.transform = `translate3d(${p.x}px,${p.y}px,0)`
        el.style.borderRadius = "8px"
      }
    },
    [collapsed, corner, tool],
  )

  useEffect(() => {
    applyPosition(true)
  }, [applyPosition])

  useEffect(() => {
    const h = () => applyPosition(false)
    window.addEventListener("resize", h, { passive: true })
    return () => window.removeEventListener("resize", h)
  }, [applyPosition])

  // ── Expand from collapsed ───────────────────────────────────────────
  const expand = useCallback(() => {
    setCollapsed(null)
    localStorage.removeItem(LS_COLLAPSED)
  }, [])

  // ── Drag handler ────────────────────────────────────────────────────
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Don't drag from buttons
      if ((e.target as HTMLElement).closest("button")) return
      e.preventDefault()

      const el = ref.current
      if (!el) return

      // If collapsed, start "pull to expand" gesture
      if (collapsed) {
        const startX = e.clientX
        const startY = e.clientY
        const threshold = 50
        let expanded = false

        const onMove = (ev: PointerEvent) => {
          if (expanded) return
          const dx = ev.clientX - startX
          const dy = ev.clientY - startY
          const isRight = collapsed.endsWith("right")
          const shouldExpand = isRight ? dx < -threshold : dx > threshold
          const shouldExpandV =
            collapsed.startsWith("top") ? dy > threshold : dy < -threshold
          if (shouldExpand || shouldExpandV) {
            expanded = true
            document.removeEventListener("pointermove", onMove)
            document.removeEventListener("pointerup", onUp)
            expand()
          }
        }
        const onUp = () => {
          document.removeEventListener("pointermove", onMove)
          document.removeEventListener("pointerup", onUp)
          if (!expanded) expand() // click = expand
        }
        document.addEventListener("pointermove", onMove)
        document.addEventListener("pointerup", onUp)
        return
      }

      // Normal drag
      const w = PANEL_W
      const h = tool ? TOOLBAR_H + PANEL_H : TOOLBAR_H
      const startPos = snapPosition(corner, w, h)
      const startX = e.clientX
      const startY = e.clientY
      let curX = startPos.x
      let curY = startPos.y
      let hasMoved = false
      let rafId: number | null = null

      el.style.transition = "none"

      const onMove = (ev: PointerEvent) => {
        hasMoved = true
        if (rafId) return
        rafId = requestAnimationFrame(() => {
          curX = startPos.x + (ev.clientX - startX)
          curY = startPos.y + (ev.clientY - startY)
          el.style.transform = `translate3d(${curX}px,${curY}px,0)`

          // Check if should collapse
          const outsideL = Math.max(0, -curX)
          const outsideR = Math.max(0, curX + w - window.innerWidth)
          const outsideT = Math.max(0, -curY)
          const outsideB = Math.max(0, curY + h - window.innerHeight)
          const hOut = Math.min(w, outsideL + outsideR)
          const vOut = Math.min(h, outsideT + outsideB)
          const areaOut = hOut * h + vOut * w - hOut * vOut
          const totalArea = w * h

          if (areaOut > totalArea * 0.35) {
            const cCorner = closestCorner(curX, curY, w, h)
            setCollapsed(cCorner)
            setCorner(cCorner)
            setTool(null)
            lsWrite(LS_COLLAPSED, cCorner)
            lsWrite(LS_POS, { corner: cCorner })
            document.removeEventListener("pointermove", onMove)
            document.removeEventListener("pointerup", onUp)
            if (rafId) cancelAnimationFrame(rafId)
          }
          rafId = null
        })
      }

      const onUp = () => {
        document.removeEventListener("pointermove", onMove)
        document.removeEventListener("pointerup", onUp)
        if (rafId) {
          cancelAnimationFrame(rafId)
          rafId = null
        }
        if (!hasMoved) return

        const c = closestCorner(curX, curY, w, h)
        setCorner(c)
        lsWrite(LS_POS, { corner: c })

        const snap = snapPosition(c, w, h)
        el.style.transition = "transform 0.25s cubic-bezier(0,0,0.2,1)"
        el.style.transform = `translate3d(${snap.x}px,${snap.y}px,0)`
      }

      document.addEventListener("pointermove", onMove)
      document.addEventListener("pointerup", onUp)
    },
    [collapsed, corner, tool, expand],
  )

  // ── Tool toggle ─────────────────────────────────────────────────────
  const toggleTool = useCallback(
    (id: ToolId) => {
      setTool((cur) => (cur === id ? null : id))
    },
    [],
  )

  const handleSubSelect = useCallback((mode: DevSubscriptionMode) => {
    setSubMode(mode)
    setDevSubscriptionOverride(mode)
  }, [])

  const clearConsole = useCallback(() => {
    logEntries = []
    for (const fn of logListeners) fn()
  }, [])

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div
      ref={ref}
      data-devtools-widget
      onPointerDown={(e) => {
        e.stopPropagation()
        handlePointerDown(e)
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 2147483647,
        pointerEvents: "auto",
        background: "#000",
        color: "#fff",
        fontFamily:
          "Menlo,Consolas,Monaco,Liberation Mono,Lucida Console,monospace",
        fontSize: 13,
        userSelect: "none",
        cursor: collapsed ? "pointer" : "move",
        overflow: "hidden",
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
        touchAction: "none",
        opacity: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {collapsed ? (
        /* ── Collapsed pill ─────────────────────────────────────────── */
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#a1a1aa"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: collapsed.endsWith("right")
                ? "rotate(180deg)"
                : "none",
            }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      ) : (
        <>
          {/* ── Toolbar row ───────────────────────────────────────── */}
          <div
            style={{
              height: TOOLBAR_H,
              display: "flex",
              alignItems: "center",
              gap: 2,
              padding: "0 6px",
              borderBottom: tool ? "1px solid #222" : "none",
              flexShrink: 0,
            }}
          >
            {/* Active indicator dot */}
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                background: "#22c55e",
                marginLeft: 4,
                marginRight: 4,
                flexShrink: 0,
              }}
            />

            <ToolbarBtn
              active={tool === "console"}
              label="Console"
              badge={
                entries.some((e) => e.level === "error")
                  ? "!"
                  : undefined
              }
              onClick={() => toggleTool("console")}
            />
            <ToolbarBtn
              active={tool === "subscription"}
              label={`$ ${subMode !== "real" ? subMode : ""}`}
              highlight={subMode !== "real"}
              onClick={() => toggleTool("subscription")}
            />
            <ToolbarBtn
              active={tool === "index"}
              label="Idx"
              onClick={() => toggleTool("index")}
            />
            <ToolbarBtn
              active={tool === "db"}
              label={`DB ${isSqliteActive() ? "SQLite" : "IDB"}`}
              highlight={isSqliteActive()}
              onClick={() => toggleTool("db")}
            />
            <ToolbarBtn
              active={tool === "device"}
              label="📱"
              onClick={() => toggleTool("device")}
            />
          </div>

          {/* ── Active panel ──────────────────────────────────────── */}
          {tool === "console" && (
            <ConsolePanel
              entries={entries}
              scrollRef={scrollRef}
              onClear={clearConsole}
              onScroll={(e) => {
                const el = e.currentTarget
                stickToBottom.current =
                  el.scrollHeight - el.scrollTop - el.clientHeight < 40
              }}
            />
          )}

          {tool === "subscription" && (
            <SubscriptionPanel
              active={subMode}
              onSelect={handleSubSelect}
            />
          )}

          {tool === "index" && <IndexPanel />}

          {tool === "db" && <DbBackendPanel />}

          {tool === "device" && <DeviceInfoPanel />}
        </>
      )}
    </div>
  )
})

// ─── Sub-components ─────────────────────────────────────────────────────

function ToolbarBtn({
  active,
  label,
  badge,
  highlight,
  onClick,
}: {
  active: boolean
  label: string
  badge?: string
  highlight?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? "#1e1e1e" : "transparent",
        color: highlight ? "#fbbf24" : active ? "#fff" : "#71717a",
        border: "none",
        borderRadius: 4,
        padding: "4px 8px",
        fontSize: 11,
        fontFamily: "inherit",
        fontWeight: 500,
        cursor: "pointer",
        position: "relative",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      {badge && (
        <span
          style={{
            position: "absolute",
            top: 0,
            right: -2,
            width: 8,
            height: 8,
            borderRadius: 4,
            background: "#ef4444",
          }}
        />
      )}
    </button>
  )
}

function ConsolePanel({
  entries,
  scrollRef,
  onClear,
  onScroll,
}: {
  entries: LogEntry[]
  scrollRef: React.RefObject<HTMLDivElement | null>
  onClear: () => void
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void
}) {
  const handleCopy = () => {
    const text = entries
      .map((e) => `${e.ts} [${e.level}] ${e.message}`)
      .join("\n")
    navigator.clipboard.writeText(text)
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 8px",
          borderBottom: "1px solid #222",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 10, color: "#71717a" }}>
          {entries.length} entries
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            type="button"
            onClick={handleCopy}
            style={{
              background: "#1e1e1e",
              color: "#a1a1aa",
              border: "none",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 10,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Copy
          </button>
          <button
            type="button"
            onClick={onClear}
            style={{
              background: "#1e1e1e",
              color: "#a1a1aa",
              border: "none",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 10,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "4px 8px",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          cursor: "text",
          userSelect: "text",
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onScroll={onScroll}
      >
        {entries.map((e) => (
          <div
            key={e.id}
            style={{
              color: LEVEL_CLR[e.level],
              borderBottom: "1px solid #111",
              padding: "2px 0",
              fontSize: 10,
              wordBreak: "break-all",
              whiteSpace: "pre-wrap",
            }}
          >
            <span style={{ color: "#52525b", marginRight: 4 }}>{e.ts}</span>
            {e.message}
          </div>
        ))}
      </div>
    </div>
  )
}

function SubscriptionPanel({
  active,
  onSelect,
}: {
  active: DevSubscriptionMode
  onSelect: (m: DevSubscriptionMode) => void
}) {
  return (
    <div style={{ padding: 12 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#71717a",
          marginBottom: 8,
        }}
      >
        Subscription Override
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {SUB_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => onSelect(m.value)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: active === m.value ? "rgba(168,85,247,0.1)" : "transparent",
              color: active === m.value ? "#a855f7" : "#d4d4d8",
              border: "none",
              borderRadius: 4,
              padding: "6px 8px",
              fontSize: 12,
              fontFamily: "inherit",
              fontWeight: active === m.value ? 600 : 400,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span>{m.emoji}</span>
            <span>{m.label}</span>
            {active === m.value && (
              <span style={{ marginLeft: "auto", fontSize: 10 }}>●</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

function IndexPanel() {
  return (
    <div style={{ padding: 12 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#71717a",
          marginBottom: 12,
        }}
      >
        Index Debug Tools
      </div>

      <button
        type="button"
        onClick={dispatchIndexToggle}
        style={{
          display: "block",
          width: "100%",
          background: "#1e1e1e",
          color: "#d4d4d8",
          border: "1px solid #333",
          borderRadius: 6,
          padding: "8px 12px",
          fontSize: 12,
          fontFamily: "inherit",
          cursor: "pointer",
          textAlign: "left",
          marginBottom: 8,
        }}
      >
        Toggle Index Badges{" "}
        <span style={{ color: "#71717a", fontSize: 10 }}>Ctrl+Shift+D</span>
      </button>

      <button
        type="button"
        onClick={() => {
          document.dispatchEvent(
            new KeyboardEvent("keydown", {
              key: "R",
              code: "KeyR",
              ctrlKey: true,
              shiftKey: true,
              bubbles: true,
            }),
          )
        }}
        style={{
          display: "block",
          width: "100%",
          background: "#1e1e1e",
          color: "#d4d4d8",
          border: "1px solid #333",
          borderRadius: 6,
          padding: "8px 12px",
          fontSize: 12,
          fontFamily: "inherit",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        Repair Indexes{" "}
        <span style={{ color: "#71717a", fontSize: 10 }}>Ctrl+Shift+R</span>
      </button>
    </div>
  )
}

const DB_BACKENDS: Array<{ value: DbBackend; label: string; desc: string }> = [
  { value: "auto", label: "Auto", desc: "Native→SQLite, Web→IDB" },
  { value: "idb", label: "IndexedDB", desc: "Force IndexedDB on all platforms" },
  { value: "sqlite", label: "SQLite", desc: "Force SQLite (Capacitor only)" },
]

function DbBackendPanel() {
  const [pref, setPref] = useState(getDbBackendPref)
  const active = resolveBackend(pref)
  const current = isSqliteActive() ? "sqlite" : "idb"

  const handleSelect = useCallback((backend: DbBackend) => {
    setPref(backend)
    setDbBackendPref(backend)
  }, [])

  const needsRestart = resolveBackend(pref) !== current

  return (
    <div style={{ padding: 12 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#71717a",
          marginBottom: 4,
        }}
      >
        Database Backend
      </div>
      <div style={{ fontSize: 10, color: "#52525b", marginBottom: 8 }}>
        Active: <span style={{ color: current === "sqlite" ? "#22c55e" : "#60a5fa" }}>
          {current === "sqlite" ? "SQLite" : "IndexedDB"}
        </span>
        {" → "}Will use: <span style={{ color: active === "sqlite" ? "#22c55e" : "#60a5fa" }}>
          {active === "sqlite" ? "SQLite" : "IndexedDB"}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {DB_BACKENDS.map((b) => (
          <button
            key={b.value}
            type="button"
            onClick={() => handleSelect(b.value)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: pref === b.value ? "rgba(34,197,94,0.1)" : "transparent",
              color: pref === b.value ? "#22c55e" : "#d4d4d8",
              border: "none",
              borderRadius: 4,
              padding: "6px 8px",
              fontSize: 12,
              fontFamily: "inherit",
              fontWeight: pref === b.value ? 600 : 400,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span style={{ minWidth: 70 }}>{b.label}</span>
            <span style={{ color: "#71717a", fontSize: 10 }}>{b.desc}</span>
            {pref === b.value && (
              <span style={{ marginLeft: "auto", fontSize: 10 }}>●</span>
            )}
          </button>
        ))}
      </div>
      {needsRestart && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            display: "block",
            width: "100%",
            marginTop: 8,
            background: "#dc2626",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 12,
            fontFamily: "inherit",
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          Restart App to Apply
        </button>
      )}
    </div>
  )
}

function DeviceInfoPanel() {
  const [deviceId, setDeviceId] = useState<string>("loading...")
  const [fcmToken, setFcmToken] = useState<string>("loading...")
  const [platform, setPlatform] = useState<string>("")
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    // Get device ID
    import("@/shared/utils/device/stableDeviceId").then(async () => {
      try {
        // Try to read from localStorage (any user)
        const keys = Object.keys(localStorage).filter((k) =>
          k.startsWith("totodo_device_id:"),
        )
        if (keys.length > 0) {
          setDeviceId(localStorage.getItem(keys[0]!) || "?")
        } else {
          const legacy = localStorage.getItem("totodo_stable_device_id")
          setDeviceId(legacy || "(not set)")
        }
      } catch {
        setDeviceId("(error)")
      }
    })

    // Get FCM token from push_token table
    import("@/db/schema").then(async ({ db: appDb }) => {
      try {
        const tokens = await appDb.table("push_token").toArray()
        const active = tokens.filter(
          (t: Record<string, unknown>) =>
            t.status === "active" && !t.isDeleted,
        )
        if (active.length > 0) {
          setFcmToken(String(active[0].token || "?"))
        } else if (tokens.length > 0) {
          setFcmToken(`(${tokens.length} tokens, none active)`)
        } else {
          setFcmToken("(no tokens)")
        }
      } catch {
        setFcmToken("(table not available)")
      }
    })

    // Platform
    import("@capacitor/core").then(({ Capacitor }) => {
      setPlatform(Capacitor.getPlatform())
    })
  }, [])

  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label)
      setTimeout(() => setCopied(null), 1500)
    })
  }, [])

  return (
    <div
      style={{ padding: 12, overflow: "auto", flex: 1, touchAction: "pan-y", cursor: "default" }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#71717a",
          marginBottom: 8,
        }}
      >
        Device Info
      </div>

      <div style={{ fontSize: 10, color: "#52525b", marginBottom: 4 }}>
        Platform: <span style={{ color: "#d4d4d8" }}>{platform || "web"}</span>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: "#52525b", marginBottom: 2 }}>
          Device ID:
        </div>
        <button
          type="button"
          onClick={() => copyToClipboard(deviceId, "deviceId")}
          style={{
            display: "block",
            width: "100%",
            background: "#1e1e1e",
            color: "#60a5fa",
            border: "1px solid #333",
            borderRadius: 4,
            padding: "6px 8px",
            fontSize: 10,
            fontFamily: "inherit",
            cursor: "pointer",
            textAlign: "left",
            wordBreak: "break-all",
          }}
        >
          {deviceId}
          {copied === "deviceId" && (
            <span style={{ color: "#22c55e", marginLeft: 8 }}>✓ copied</span>
          )}
        </button>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: "#52525b", marginBottom: 2 }}>
          FCM Token:
        </div>
        <button
          type="button"
          onClick={() => copyToClipboard(fcmToken, "fcmToken")}
          style={{
            display: "block",
            width: "100%",
            background: "#1e1e1e",
            color: "#fbbf24",
            border: "1px solid #333",
            borderRadius: 4,
            padding: "6px 8px",
            fontSize: 10,
            fontFamily: "inherit",
            cursor: "pointer",
            textAlign: "left",
            wordBreak: "break-all",
            maxHeight: 80,
            overflow: "auto",
          }}
        >
          {fcmToken.length > 60
            ? `${fcmToken.slice(0, 30)}...${fcmToken.slice(-20)}`
            : fcmToken}
          {copied === "fcmToken" && (
            <span style={{ color: "#22c55e", marginLeft: 8 }}>✓ copied</span>
          )}
        </button>
      </div>

      <div style={{ fontSize: 9, color: "#52525b", marginTop: 8 }}>
        Tap to copy. Full token in console on copy.
      </div>
    </div>
  )
}
